import { useEffect, useState } from 'react';

export const getPersistedValue = <Value>(
  storageString: string,
  defaultValue: Value,
  storageProvider: Storage = localStorage
) => {
  try {
    const value = storageProvider.getItem(storageString);
    return (value && JSON.parse(value)) ?? defaultValue;
  } catch (error) {
    console.error(
      'ProseMirror Local Storage Read Error',
      error,
      storageString,
      defaultValue
    );
    return defaultValue;
  }
};

export const setPersistedValue = <Value>(
  storageString: string,
  value: Value,
  storageProvider: Storage = localStorage
) => {
  try {
    storageProvider.setItem(storageString, JSON.stringify(value));
  } catch (error) {
    console.error(
      'ProseMirror Local Storage Write Error',
      error,
      storageString,
      value
    );
  }
};

interface HookOptions {
  /** Disable storage mechanism if false. Makes the hook behave like a regular useState(). */
  persist: boolean;
  /** Allow the caller to override the Storage object for persistence operations. */
  storageProvider: Storage;
}

export const usePersistedValue = <Value>(
  key: string,
  defaultValue: Value,
  options: Partial<HookOptions> = {}
) => {
  const { persist = true, storageProvider = localStorage } = options;
  const [state, setState] = useState<Value>(() => {
    return persist
      ? getPersistedValue(key, defaultValue, storageProvider)
      : defaultValue;
  });
  useEffect(() => {
    if (persist) {
      setPersistedValue(key, state, storageProvider);
    }
  }, [state, key, persist, storageProvider]);
  return [state, setState] as const;
};
